import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ImagePlus, Replace } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import '../../styles/module-shared.css';
import './education.css';
import {
  COURSE_LEVELS,
  COURSE_STATUSES,
  THUMBNAIL_ACCEPT,
  emptyCourseForm,
  normalizeCourse,
  toCoursePayload,
  validateCourseForm,
  validateThumbnailFile,
} from './courseUtils';

const THEME = getThemeColor('primary');

const DESC_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const DESC_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'align',
  'link',
];

const CourseForm = () => {
  const { courseId } = useParams();
  const isEdit = Boolean(courseId);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(emptyCourseForm());
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');

  const quillModules = useMemo(() => DESC_MODULES, []);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiService.getEducationCourseById(courseId);
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || res.message || 'Failed to load course');
          return;
        }
        const course = normalizeCourse(res.data ?? res.result);
        if (!course) {
          setError('Course not found');
          return;
        }
        setForm({
          title: course.title,
          shortDescription: course.shortDescription,
          description: course.description,
          category: course.category,
          level: course.level || 'Beginner',
          duration: course.duration,
          language: course.language,
          status: course.status || 'Active',
        });
        setExistingThumbnailUrl(course.thumbnailUrl || '');
        setThumbnailFile(null);
        setThumbnailPreview('');
      } catch (err) {
        if (!cancelled) setError(toUserMessage(err, ERROR_MESSAGES.load));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, isEdit]);

  useEffect(() => {
    if (!thumbnailFile) {
      setThumbnailPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (formError) setFormError('');
  };

  const openFilePicker = () => {
    if (saving) return;
    fileInputRef.current?.click();
  };

  const onThumbnailSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const validation = validateThumbnailFile(file);
    if (validation) {
      setFormError(validation);
      showError(validation);
      return;
    }
    setFormError('');
    setThumbnailFile(file);
  };

  const previewSrc = thumbnailPreview || existingThumbnailUrl;
  const hasPreview = Boolean(previewSrc);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateCourseForm(form);
    if (validation) {
      setFormError(validation);
      return;
    }
    if (thumbnailFile) {
      const thumbErr = validateThumbnailFile(thumbnailFile);
      if (thumbErr) {
        setFormError(thumbErr);
        return;
      }
    }

    setSaving(true);
    setFormError('');
    try {
      const payload = toCoursePayload(form);
      const res = isEdit
        ? await apiService.updateEducationCourse(
            { courseId: Number(courseId), ...payload },
            thumbnailFile
          )
        : await apiService.createEducationCourse(payload, thumbnailFile);

      if (!res.success) {
        setFormError(res.error || res.message || 'Save failed');
        return;
      }

      showSuccess(res.message || (isEdit ? 'Course updated' : 'Course created'));
      const created = normalizeCourse(res.data ?? res.result);
      if (!isEdit && created?.id) {
        navigate(`/education/courses/${created.id}`);
      } else if (isEdit) {
        navigate(`/education/courses/${courseId}`);
      } else {
        navigate('/education/courses');
      }
    } catch (err) {
      setFormError(toUserMessage(err, ERROR_MESSAGES.save));
      showError(toUserMessage(err, ERROR_MESSAGES.save));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page edu-page" style={{ '--edu-primary': THEME }}>
      <div className="page-inner">
        <button type="button" className="edu-back" onClick={() => navigate('/education/courses')}>
          <ArrowLeft className="w-4 h-4" />
          Back to courses
        </button>

        <ModulePageHeader
          icon={BookOpen}
          title={isEdit ? 'Edit Course' : 'Add Course'}
          subtitle="Basic information for your education course"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />

        <ModuleAlert message={error} />

        <section className="module-card edu-section">
          <form onSubmit={handleSubmit}>
            <div className="edu-form-grid">
              <div className="edu-field edu-field--full">
                <label className="edu-label" htmlFor="edu-title">
                  Course Title *
                </label>
                <input
                  id="edu-title"
                  className="edu-input"
                  value={form.title}
                  onChange={(e) => setField('title', e.target.value)}
                  disabled={saving}
                  required
                />
              </div>

              <div className="edu-field edu-field--full">
                <label className="edu-label" htmlFor="edu-short">
                  Short Description
                </label>
                <input
                  id="edu-short"
                  className="edu-input"
                  value={form.shortDescription}
                  onChange={(e) => setField('shortDescription', e.target.value)}
                  disabled={saving}
                  maxLength={500}
                />
              </div>

              <div className="edu-field edu-field--full">
                <span className="edu-label">Full Description</span>
                <div className={`edu-quill ${saving ? 'is-disabled' : ''}`}>
                  <ReactQuill
                    theme="snow"
                    value={form.description || ''}
                    onChange={(value) => setField('description', value)}
                    modules={quillModules}
                    formats={DESC_FORMATS}
                    readOnly={saving}
                    placeholder="Write a detailed course description…"
                  />
                </div>
              </div>

              <div className="edu-field edu-field--full">
                <span className="edu-label">
                  {isEdit && existingThumbnailUrl && !thumbnailFile
                    ? 'Current Thumbnail'
                    : 'Course Thumbnail'}
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={THUMBNAIL_ACCEPT}
                  className="edu-file-input"
                  onChange={onThumbnailSelected}
                  disabled={saving}
                />

                {hasPreview ? (
                  <div className="edu-thumb-upload">
                    <div className="edu-thumb-upload__preview">
                      <img src={previewSrc} alt="Course thumbnail preview" />
                    </div>
                    <div className="edu-thumb-upload__actions">
                      <button
                        type="button"
                        className="edu-btn edu-btn--ghost"
                        onClick={openFilePicker}
                        disabled={saving}
                      >
                        <Replace className="w-4 h-4" />
                        {isEdit || existingThumbnailUrl ? 'Replace Image' : 'Change Image'}
                      </button>
                      {thumbnailFile ? (
                        <button
                          type="button"
                          className="edu-btn edu-btn--ghost"
                          onClick={() => setThumbnailFile(null)}
                          disabled={saving}
                        >
                          Remove selection
                        </button>
                      ) : null}
                    </div>
                    <p className="edu-thumb-upload__hint">JPG, PNG or WEBP · max 5 MB</p>
                  </div>
                ) : (
                  <div className="edu-thumb-upload edu-thumb-upload--empty">
                    <button
                      type="button"
                      className="edu-btn edu-btn--primary"
                      onClick={openFilePicker}
                      disabled={saving}
                    >
                      <ImagePlus className="w-4 h-4" />
                      Upload Image
                    </button>
                    <p className="edu-thumb-upload__hint">JPG, PNG or WEBP · max 5 MB</p>
                  </div>
                )}
              </div>

              <div className="edu-field">
                <label className="edu-label" htmlFor="edu-category">
                  Category *
                </label>
                <input
                  id="edu-category"
                  className="edu-input"
                  value={form.category}
                  onChange={(e) => setField('category', e.target.value)}
                  disabled={saving}
                  required
                />
              </div>

              <div className="edu-field">
                <label className="edu-label" htmlFor="edu-level">
                  Level *
                </label>
                <select
                  id="edu-level"
                  className="edu-select"
                  value={form.level}
                  onChange={(e) => setField('level', e.target.value)}
                  disabled={saving}
                  required
                >
                  {COURSE_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>

              <div className="edu-field">
                <label className="edu-label" htmlFor="edu-duration">
                  Duration
                </label>
                <input
                  id="edu-duration"
                  className="edu-input"
                  value={form.duration}
                  onChange={(e) => setField('duration', e.target.value)}
                  disabled={saving}
                  placeholder="e.g. 2h 30m"
                />
              </div>

              <div className="edu-field">
                <label className="edu-label" htmlFor="edu-language">
                  Language
                </label>
                <input
                  id="edu-language"
                  className="edu-input"
                  value={form.language}
                  onChange={(e) => setField('language', e.target.value)}
                  disabled={saving}
                  placeholder="e.g. English"
                />
              </div>

              <div className="edu-field">
                <label className="edu-label" htmlFor="edu-status">
                  Status
                </label>
                <select
                  id="edu-status"
                  className="edu-select"
                  value={form.status}
                  onChange={(e) => setField('status', e.target.value)}
                  disabled={saving}
                >
                  {COURSE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {formError ? <span className="edu-form-error">{formError}</span> : null}

            <div className="edu-actions-row">
              <button
                type="button"
                className="edu-btn edu-btn--ghost"
                disabled={saving}
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button type="submit" className="edu-btn edu-btn--primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Update Course' : 'Create Course'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default CourseForm;
